import { getVariables } from '../../requests/documentDatas';
import { buildVariableCatalog } from './variableCatalog';

/**
 * Registre module-level du catalogue de variables.
 *
 * Il vit **hors** du store Puck (invariant §4.3 du README) : le menu `@` du
 * canvas et la barre d'outils le lisent par `useSyncExternalStore`, sans que
 * la frappe ne remonte d'état dans les composants parents.
 *
 * Chargement **paresseux** : aucun appel API à l'ouverture de l'éditeur, le
 * catalogue n'est lu qu'au premier `@` tapé ou au clic sur « Rafraîchir les
 * variables » (décision du 2026-08-05).
 */

const EMPTY_CATALOG = { byKey: {}, groups: [] };

let source = null;
let catalog = EMPTY_CATALOG;
let status = 'idle';
let error = null;
let inFlight = null;
let snapshot = { catalog: EMPTY_CATALOG, status: 'idle', error: null };

const listeners = new Set();

function emit() {
  snapshot = { catalog, status, error };
  listeners.forEach((listener) => listener());
}

/**
 * Identité du document qui fournit les variables : `{ id, type, params? }`.
 * `params` fait partie de l'identité — le template de site web change de
 * catalogue quand on passe d'« opportunité » à « offre », à id constant.
 */
export function setVariableCatalogSource(next) {
  const nextId = next?.id != null ? String(next.id) : null;
  const nextType = next?.type || null;
  const nextParams = next?.params || null;
  if (source?.id === nextId
    && source?.type === nextType
    && JSON.stringify(source?.params || null) === JSON.stringify(nextParams)) return;
  source = nextId && nextType
    ? { id: nextId, type: nextType, ...(nextParams ? { params: nextParams } : {}) }
    : null;
  catalog = EMPTY_CATALOG;
  status = 'idle';
  error = null;
  inFlight = null;
  emit();
}

export function getVariableCatalogSource() {
  return source;
}

/** Le catalogue est-il celui d'un template de site (et non d'un site) ? */
export function isWebTemplateCatalog() {
  return source?.type === 'web_template';
}

export function subscribeVariableCatalog(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getVariableCatalogSnapshot() {
  return snapshot;
}

/**
 * Charge le catalogue (une seule requête en vol à la fois).
 *
 * L'échec reste **visible** : `status: 'error'` dans le registre et une trace
 * console. C'est le `.catch(() => ({}))` silencieux de l'ancien
 * `fetchProposalVariables` qui avait masqué une 404 pendant des mois.
 *
 * @param {{ force?: boolean }} [options] - `force` ignore le cache (bouton « Rafraîchir »)
 * @returns {Promise<{ byKey: object, groups: Array }>}
 */
export function loadVariableCatalog({ force = false } = {}) {
  // Aucune source : un modèle pas encore enregistré, ou un web_data sans
  // `documentIdentity`. Le menu `@` le dit au lieu d'afficher une liste vide.
  // Un modèle enregistré, lui, A une source (`type: 'web_template'`) : le back
  // sert ses variables sans valeur, ce qui suffit à les choisir par leur nom.
  if (!source) {
    if (status !== 'unavailable') {
      status = 'unavailable';
      error = null;
      emit();
    }
    return Promise.resolve(EMPTY_CATALOG);
  }
  if (!force && status === 'ready') return Promise.resolve(catalog);
  if (inFlight && !force) return inFlight;

  const requested = source;
  status = 'loading';
  error = null;
  emit();

  inFlight = getVariables(requested)
    .then((raw) => {
      // Requête d'un document précédent : son résultat ne doit pas s'installer.
      if (requested !== source) return catalog;
      if (!raw) throw new Error('catalogue de variables indisponible');
      catalog = buildVariableCatalog(raw);
      status = 'ready';
      error = null;
      emit();
      return catalog;
    })
    .catch((e) => {
      if (requested !== source) return catalog;
      console.error('[webviewer] chargement du catalogue de variables', requested, e);
      status = 'error';
      error = e;
      emit();
      return EMPTY_CATALOG;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Remise à zéro complète — réservée aux tests. */
export function resetVariableCatalogRegistry() {
  source = null;
  catalog = EMPTY_CATALOG;
  status = 'idle';
  error = null;
  inFlight = null;
  snapshot = { catalog: EMPTY_CATALOG, status: 'idle', error: null };
  listeners.clear();
}
